import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import AppHeader from "@/components/AppHeader";
import BackButton from "@/components/BackButton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Trophy, MapPin, HeartHandshake, Star } from "lucide-react";
import { Badge } from "@/components/ui/badge";

/**
 * Leaderboards: Users · Territories · Generous provinces (donations to reports).
 * Positive scoring only — territories with more problems are NOT ridiculed.
 */
export default function Leaderboard() {
  const { t, fmtCurrency } = useI18n();
  const [tab, setTab] = useState("users");
  const [period, setPeriod] = useState("month");
  const [users, setUsers] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [generous, setGenerous] = useState([]);

  useEffect(() => {
    api.get("/leaderboard").then((r) => setUsers(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (tab === "territories") api.get("/leaderboards/territories", { params: { period } }).then((r) => setTerritories(r.data || []));
    if (tab === "generous") api.get("/leaderboards/generous-provinces", { params: { period } }).then((r) => setGenerous(r.data || []));
  }, [tab, period]);

  const TabBtn = ({ id, label, icon: Icon }) => (
    <button
      data-testid={`lb-tab-${id}`}
      onClick={() => setTab(id)}
      className={`px-3 py-2 rounded-full text-xs font-semibold border-2 flex items-center gap-1.5 ${tab === id ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`}
    >
      <Icon size={12} /> {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="leaderboard-page">
      <AppHeader />
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        <BackButton className="mb-4" testid="lb-back-btn" />
        <div className="text-xs uppercase tracking-[0.25em] text-emerald-700 font-semibold flex items-center gap-2"><Trophy size={12} /> {t("nav_leaderboard")}</div>
        <h1 className="font-heading text-4xl font-bold text-slate-900 mt-2">{t("classifications_title")}</h1>
        <p className="text-sm text-slate-600 mt-1">{t("lb_subtitle")}</p>

        <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="lb-tabs">
          <TabBtn id="users" label={t("tab_users")} icon={Star} />
          <TabBtn id="territories" label={t("tab_territories")} icon={MapPin} />
          <TabBtn id="generous" label={t("tab_generous")} icon={HeartHandshake} />
          {tab !== "users" && (
            <div className="ml-auto flex items-center gap-1" data-testid="lb-period">
              {["week","month","year","all"].map((p) => (
                <button key={p} data-testid={`lb-period-${p}`} onClick={() => setPeriod(p)} className={`text-xs px-2.5 h-7 rounded-full border ${period === p ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                  {p === "week" ? t("period_week") : p === "month" ? t("period_month") : p === "year" ? t("period_year") : t("period_all")}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 bg-white border border-slate-200 rounded-2xl overflow-hidden">
          {tab === "users" && users.map((u, i) => (
            <Link key={u.user_id} to={`/profile/${u.user_id}`} data-testid={`lb-row-${i}`} className={`flex items-center gap-4 p-4 ${i > 0 ? "border-t border-slate-100" : ""} hover:bg-emerald-50/50`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-heading font-bold text-sm ${i < 3 ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"}`}>{i + 1}</div>
              <Avatar className="w-10 h-10">
                <AvatarImage src={u.picture} />
                <AvatarFallback className="bg-emerald-100 text-emerald-800 text-sm">{u.name?.[0]?.toUpperCase() || "?"}</AvatarFallback>
              </Avatar>
              <div className="flex-1"><div className="font-semibold text-slate-900">{u.name}</div><div className="text-xs text-slate-500">{u.reputation || 0} pts</div></div>
            </Link>
          ))}

          {tab === "territories" && territories.length === 0 && (
            <div className="p-8 text-center text-slate-500 text-sm" data-testid="lb-territories-empty">{t("no_period_data")}</div>
          )}
          {tab === "territories" && territories.map((r) => (
            <div key={r.province_gid} className="flex items-center gap-3 p-4 border-t border-slate-100 first:border-t-0" data-testid={`lb-terr-row-${r.rank}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-heading font-bold text-sm ${r.rank <= 3 ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"}`}>{r.rank}</div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900">{r.province_name || "—"} <span className="text-xs text-slate-500 font-normal">· {r.country}</span></div>
                <div className="text-xs text-slate-500 mt-0.5">{r.resolved} risolti · {r.active_open} attivi · {r.resolution_pct}%</div>
              </div>
              <div className="text-right">
                <div className="font-heading text-lg font-bold text-emerald-700 tabular-nums">{r.score}</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-widest">Score</div>
              </div>
            </div>
          ))}

          {tab === "generous" && generous.length === 0 && (
            <div className="p-8 text-center text-slate-500 text-sm" data-testid="lb-generous-empty">Nessuna donazione registrata nel periodo selezionato.</div>
          )}
          {tab === "generous" && generous.map((r) => (
            <div key={r.province_gid} className="flex items-center gap-3 p-4 border-t border-slate-100 first:border-t-0" data-testid={`lb-gen-row-${r.rank}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-heading font-bold text-sm ${r.rank === 1 ? "bg-yellow-500 text-white" : r.rank <= 3 ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"}`}>{r.rank}</div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 flex items-center gap-2">
                  {r.province_name || "—"}
                  {r.badge === "most_generous" && <Badge className="bg-yellow-100 text-yellow-800 border-0 text-[10px]">{t("most_generous_province_badge")}</Badge>}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{r.donation_count} donazioni · {r.reports_supported} segnalazioni sostenute · {r.country}</div>
              </div>
              <div className="text-right">
                <div className="font-heading text-lg font-bold text-emerald-700 tabular-nums">{fmtCurrency(r.total_amount)}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 text-[11px] text-slate-500 italic">Sono conteggiate solo le donazioni effettivamente incassate. Rimborsi e pagamenti falliti non vengono considerati. L'identità dei singoli donatori non è mai pubblica.</div>
      </div>
    </div>
  );
}
